/**
 * HelpCenterSection Component
 * Pixel-perfect match of Figma node 3598-30155
 *
 * Vertical order: Title → Description → Knowledge Base → Get Support →
 * Live Chat → "Check out Resources..." link → Divider → CEO card (photo left, text right)
 *
 * Spacing: 5px title→desc, 30px between all cards/sections, 35px divider→CEO
 * Buttons: 200×50, bg #F2F0EB / text #020202, except Chat Now (outline, text #F2F0EB)
 * Colors: all text #F2F0EB on dark background
 */

import { Button } from "@/components/ui/button"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"

export function HelpCenterSection() {
  const { t } = useTranslation("settings")

  const handleAction = (_action: string) => {
    // TODO: Implement support action handlers
  }

  return (
    <div className="flex flex-col text-[#F2F0EB] w-full">
      {/* 1. Title — fs:20, fw:600 */}
      <h1 className="text-[20px] font-semibold leading-[20px]">{t("helpCenter.title")}</h1>

      {/* 2. Description — fs:14, fw:400, 5px gap from title */}
      <p className="text-sm leading-[25px] mt-[5px]">{t("helpCenter.description")}</p>

      {/* 3. Knowledge Base — 30px gap, 70px card */}
      <div className="mt-[30px] flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0 w-full">
        <div className="flex flex-col gap-0">
          <h3 className="text-[15px] font-semibold leading-[25px]">
            {t("helpCenter.knowledgeBase")}
          </h3>
          <p className="text-sm leading-[20px] whitespace-pre-line">
            {t("helpCenter.knowledgeBaseDesc")}
          </p>
        </div>
        <Button
          className="shrink-0 h-[50px] w-full md:w-[200px] rounded-full bg-[#F2F0EB] text-[#020202] text-sm font-semibold hover:bg-[#F2F0EB]/90 border-0"
          onClick={() => handleAction("knowledgeBase")}
        >
          {t("helpCenter.knowledgeBase")}
        </Button>
      </div>

      {/* 4. Get Support — 30px gap, 70px card */}
      <div className="mt-[30px] flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0">
        <div className="flex flex-col gap-0">
          <h3 className="text-[15px] font-semibold leading-[25px]">{t("helpCenter.getSupport")}</h3>
          <p className="text-sm leading-[20px] whitespace-pre-line">
            {t("helpCenter.getSupportDesc")}
          </p>
        </div>
      </div>

      {/* 5. Live Chat Support — 30px gap, outline button */}
      <div className="mt-[30px] flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0">
        <div className="flex flex-col gap-0">
          <h3 className="text-[15px] font-semibold leading-[25px]">{t("helpCenter.liveChat")}</h3>
          <p className="text-sm leading-[20px] whitespace-pre-line">
            {t("helpCenter.liveChatDesc")}
          </p>
        </div>
        <Button
          disabled
          className="shrink-0 h-[50px] w-full md:w-[200px] rounded-full bg-[#F2F0EB] text-[#020202] text-sm font-semibold border-0 opacity-50 cursor-not-allowed"
        >
          {t("helpCenter.chatNow")}
        </Button>
      </div>

      {/* 6. Resources link — 30px gap */}
      <p className="mt-[30px] text-sm leading-[25px] text-primary opacity-70">
        {t("helpCenter.resourcesLink")}
      </p>

      {/* 7. Divider — 30px gap */}
      <div className="mt-[30px] w-full shrink-0 h-[1px] bg-[#F2F0EB] opacity-20" />

      {/* 8. Talk with Rituality CEO — 35px gap, photo left + text right */}
      <div className="mt-[35px] flex flex-col md:flex-row md:items-start gap-[22px]">
        {/* CEO Profile Photo — 130×130, asymmetric corners (tr+bl only), 2px border */}
        <div className="relative w-[130px] h-[130px] shrink-0 rounded-tr-[30px] rounded-bl-[30px]">
          <img
            src={getAssetUrl("ceo-image.png")}
            alt={t("helpCenter.ceoName")}
            className="object-cover rounded-tr-[30px] rounded-bl-[30px] w-[130px] h-[130px]"
          />

          <div
            className="absolute bottom-2 left-2  border-2 border-[#F2F0EB] rounded-tr-[38px] rounded-bl-[30px] opacity-30 w-[130px]
h-[130px]"
          ></div>
        </div>

        {/* CEO Content — text on left, button on right */}
        <div className="flex-1 flex flex-col md:flex-row md:items-start justify-between gap-4 md:gap-0 mb-10">
          <div className="flex flex-col gap-2">
            <h3 className="text-[15px] font-semibold leading-[25px]">{t("helpCenter.ceoTitle")}</h3>
            <p className="text-sm leading-[20px] mt-0 whitespace-pre-line">
              {t("helpCenter.ceoDesc")}
            </p>
            <div>
              <p className="text-sm font-normal leading-[20px] mt-0 text-primary opacity-70">
                {t("helpCenter.ceoName")}
              </p>
              <p className="text-sm font-normal leading-[20px] mt-0 text-primary opacity-70">
                {t("helpCenter.ceoRole")}
              </p>
            </div>
          </div>
          <Button
            className="shrink-0 h-[50px] w-full md:w-[200px] rounded-full bg-[#F2F0EB] text-[#020202] text-sm font-semibold hover:bg-[#F2F0EB]/90 border-0"
            onClick={() =>
              window.open(
                "https://calendar.google.com/calendar/u/0/appointments/AcZssZ0ki3Eo5ROALBtarq11Bofbj5owGFFWQ59DG1k=",
                "_blank",
              )
            }
          >
            {t("helpCenter.ceoButton")}
          </Button>
        </div>
      </div>
    </div>
  )
}
